import { useEffect, useMemo, useRef, useState } from 'react';

import type { UserLauncherItemDto } from 'utils/api/dto';
import * as endpoints from 'utils/api/endpoints';
import { httpMethods, useFetchData } from 'utils/hooks/use-fetch-data';

import {
    clearLocalHeaderPins,
    getLocalHeaderPins,
    hasPendingHeaderPins,
    savePendingHeaderPins,
    setHeaderPinsSynced,
    setSyncedHeaderPins
} from '../header-pins';
import { applyLauncherItemResponse, nextLauncherItemRequest, sortLauncherItems } from '../launcher-items';
import type { LauncherItemRequest } from '../launcher-items';
import { useStoredHeaderPins } from './use-stored-header-pins';

// A slow network fails a request instead of holding the queue: the items are reloaded then
const REQUEST_TIMEOUT_MS = 15000;
// Loading again after a failure: 5 s, 10 s, 20 s… up to a minute; at once when the browser is back online
const RETRY_DELAY_MS = 5000;
const MAX_RETRY_DELAY_MS = 60000;

// Worth loading again: no answer (offline, timeout) or a server error; another answer (403, 404) will not change
// by itself, and the pins stay in local storage
const isTemporaryError = (error: unknown) => {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;

    return status == null || status >= 500 || status === 408 || status === 429;
};

interface UseLauncherItemsSyncParams {
    userId: number;
}

/**
 * Keeps the header pins of a signed-in user and their launcher items on the backend the same: loads the items into
 * the pins, then sends the changes of the pins one request at a time. Mounted once (LauncherItemsSync).
 * While the items are not loaded the pins are saved to local storage, marked as pending, and sent once the backend
 * answers; local storage is cleared when the backend has the pins. A request that failed is kept as pending too and
 * sent again after a pause (5 s, 10 s… up to a minute), so a bad network never drops the user's pins.
 */
export const useLauncherItemsSync = ({ userId }: UseLauncherItemsSyncParams) => {
    const pins = useStoredHeaderPins();
    // The items on the backend; null until loaded and while reloading, nothing is sent then
    const [items, setItems] = useState<UserLauncherItemDto[] | null>(null);
    const [sentRequest, setSentRequest] = useState<LauncherItemRequest | null>(null);
    const [loadFailures, setLoadFailures] = useState(0);
    const loadedRef = useRef(false);

    const { handleRepeat: reloadItems } = useFetchData({
        method: httpMethods.get,
        url: endpoints.userLauncherItems,
        timeout: REQUEST_TIMEOUT_MS,
        responseHandler: (response: unknown) => {
            const loaded = sortLauncherItems(response);
            const paths = loaded.map(item => item.path);
            const localPins = getLocalHeaderPins(userId);
            // Local pins win over the backend when they are changes it has not got, or on the first load when the
            // backend has none yet (the pins this browser kept before the backend did)
            const useLocal =
                !!localPins &&
                (hasPendingHeaderPins(userId) || (!loadedRef.current && !paths.length && localPins.length > 0));
            loadedRef.current = true;

            // Pins first: the updates are not batched outside React events, and the items start the sync
            setSyncedHeaderPins(useLocal && localPins ? localPins : paths, userId);
            setHeaderPinsSynced(userId, true);
            setItems(loaded);
        },
        onRequestError: (error: unknown) => {
            if (isTemporaryError(error)) setLoadFailures(failures => failures + 1);
        }
    });

    const { handleRepeat: send } = useFetchData({
        method: sentRequest?.method,
        url: sentRequest?.url,
        payload: sentRequest && 'payload' in sentRequest ? sentRequest.payload : null,
        initialCondition: false,
        isDisabled: !sentRequest,
        timeout: REQUEST_TIMEOUT_MS,
        responseHandler: (response: unknown) => {
            const nextItems = items && sentRequest ? applyLauncherItemResponse(items, sentRequest, response) : null;
            // Items first: the updates are not batched outside React events, and the next request is chosen
            // as soon as sentRequest is cleared
            setItems(nextItems);
            setSentRequest(null);
            if (!nextItems) reloadItems();
        },
        onRequestError: () => {
            // The change is kept as pending and the items are loaded again after a pause; until then the changes go
            // to local storage
            setHeaderPinsSynced(userId, false);
            if (pins) savePendingHeaderPins(pins, userId);
            setItems(null);
            setSentRequest(null);
            setLoadFailures(failures => failures + 1);
        }
    });

    const nextRequest = useMemo(() => (items && pins && !sentRequest ? nextLauncherItemRequest(items, pins) : null), [
        items,
        pins,
        sentRequest
    ]);

    // One request at a time: the next one is chosen when the previous one has finished
    useEffect(() => {
        if (nextRequest) setSentRequest(nextRequest);
    }, [nextRequest]);

    // The backend has the pins: the copy in local storage is not needed any more, the failures are over
    const isSynced = !!items && !!pins && !sentRequest && !nextRequest;
    useEffect(() => {
        if (!isSynced) return;

        clearLocalHeaderPins(userId);
        setLoadFailures(0);
    }, [isSynced, userId]);

    // Unmounted (another user, the header hidden) with changes not sent yet: they are kept as pending
    const unsentPinsRef = useRef<string[] | null>(null);
    unsentPinsRef.current = items && pins && !isSynced ? pins : null;
    useEffect(
        () => () => {
            setHeaderPinsSynced(userId, false);
            if (unsentPinsRef.current) savePendingHeaderPins(unsentPinsRef.current, userId);
        },
        [userId]
    );

    // Not loaded because of the network: load again later, or at once when the browser is back online
    useEffect(() => {
        if (items || !loadFailures) return undefined;

        const delay = Math.min(RETRY_DELAY_MS * 2 ** (loadFailures - 1), MAX_RETRY_DELAY_MS);
        const timer = window.setTimeout(reloadItems, delay);
        window.addEventListener('online', reloadItems);

        return () => {
            window.clearTimeout(timer);
            window.removeEventListener('online', reloadItems);
        };
    }, [items, loadFailures, reloadItems]);

    useEffect(() => {
        if (sentRequest) send();
    }, [sentRequest, send]);
};
